import _, { intersection } from 'lodash';
import {
    ProtectionT,
    ScopeT,
    ExpressionT,
    UserScopeT,
} from 'business/definitions';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { TFunction } from 'react-i18next';
import { getTableName } from './helpers';

const getFields = (
    list: (
        | QueryFilterT
        | SentenceDimensionT
        | SentenceFilterT
        | SentenceMetricT
    )[],
): string[] => list.map((item) => item.field);

type FieldsByScopeT = Record<string, string[]>;

/*
    We compute the list of fields used inside a given list of widgets
    In a list of fields per scope that need to be protected,
    we filter which of the fields used in the widgets need protection
*/
export const getProtectedFieldsByScope = (
    widgets: WidgetT[],
    protections: ProtectionT[],
): FieldsByScopeT => {
    const fields = _(widgets)
        .map(({ chart }) => {
            if (!chart) return [];

            return [
                ...getFields(chart.dimensions),
                ...getFields(chart.filters),
                ...getFields(chart.metrics),
            ];
        })
        .flattenDeep()
        .uniq()
        .value();

    return protections.reduce(
        (output, protection) => ({
            ...output,
            [protection.scope]: intersection(fields, protection.blocks),
        }),
        {},
    );
};

export const isAnyExpression = (expression: ExpressionT): boolean =>
    !!expression.any;

export const isAllExpression = (expression: ExpressionT): boolean =>
    !!expression.all;

export const isBlockExpression = (expression: ExpressionT): boolean =>
    !!expression.block;

const getAnyExpressionBlocks = (expression: ExpressionT): string[] =>
    expression.any?.map((item) => item.block) || [];

export const isAnyExpressionProtectedByFields = (
    expression: ExpressionT,
    fields: string[],
): boolean => {
    const blocks = getAnyExpressionBlocks(expression);

    return !blocks.length || blocks.some((block) => fields.includes(block));
};

const getAllExpressionBlocks = (expression: ExpressionT): string[] =>
    expression.all?.map((item) => item.block) || [];

export const isAllExpressionProtectedByFields = (
    expression: ExpressionT,
    fields: string[],
): boolean => {
    const blocks = getAllExpressionBlocks(expression);

    return !blocks.length || blocks.every((block) => fields.includes(block));
};

const getBlockExpressionBlocks = (expression: ExpressionT): string[] => [
    expression.block || '',
];

export const isBlockExpressionProtectedByFields = (
    expression: ExpressionT,
    fields: string[],
): boolean => {
    const blocks = getBlockExpressionBlocks(expression);

    return !blocks.length || fields.includes(blocks[0]);
};

const isProtectedByFields = ({ expression }: ScopeT, fields: string[]) => {
    if (!expression) return true;

    if (isAnyExpression(expression)) {
        return isAnyExpressionProtectedByFields(expression, fields);
    }

    if (isAllExpression(expression)) {
        return isAllExpressionProtectedByFields(expression, fields);
    }

    if (isBlockExpression(expression)) {
        return isBlockExpressionProtectedByFields(expression, fields);
    }

    console.error('Type of expression is not supported', expression);
    return false;
};

/*
    We compute the fields that need to be protected for each given scope
    We compute the fields that are protected by the given filters
    Then we check which of the scopes are fully protected
*/
export const getProtectedScopes = (
    scopes: ScopeT[],
    filters: QueryFilterT[],
): ScopeT[] => {
    const fields = filters.map((filter) => filter.field);

    return scopes.filter((scope) =>
        isProtectedByFields(
            scope,
            fields.map((field) => getTableName(field)),
        ),
    );
};

export const getBlocks = ({ expression }: ScopeT): string[] => {
    if (!expression) return [];

    if (isAnyExpression(expression)) {
        return getAnyExpressionBlocks(expression);
    }

    if (isAllExpression(expression)) {
        return getAllExpressionBlocks(expression);
    }

    if (isBlockExpression(expression)) {
        return getBlockExpressionBlocks(expression);
    }

    return [];
};

/*
    For each scope, we compute the fields that
    are not protected by the dashboard filters
    whereas they need to be protected
*/
export const getUnprotectedFieldsByScope = (
    protectedScopes: ScopeT[],
    protectedFields: FieldsByScopeT,
): FieldsByScopeT => {
    return Object.keys(protectedFields).reduce(
        (output: FieldsByScopeT, scopeName: string) => {
            const fields = protectedScopes
                .map((scope) => scope.name)
                .includes(scopeName)
                ? []
                : protectedFields[scopeName];

            return {
                ...output,
                [scopeName]: fields,
            };
        },
        {},
    );
};

/*
    For each given scope, we return the filters that are missing
    from the given list of filters if the scope has unprotected fields
*/
export const getMissingFiltersByScope = (
    scopes: ScopeT[],
    filters: QueryFilterT[],
    unprotectedFields: FieldsByScopeT,
): FieldsByScopeT => {
    const fields = getFields(filters);
    return scopes.reduce((output, scope) => {
        const unprotectedFieldsForScope = unprotectedFields[scope.name];
        return {
            ...output,

            [scope.name]: unprotectedFieldsForScope?.length
                ? getBlocks(scope).filter((block) => !fields.includes(block))
                : [],
        };
    }, {});
};

export type ValuesByFieldT = { field: string; values: string[] };

/*
    For a given user scope, returns each value required by field
*/
export const getScopeValuesByField = (
    userScope: UserScopeT,
): ValuesByFieldT[] =>
    userScope.blocks.reduce((output: ValuesByFieldT[], block) => {
        if (!block.values || !block.values.length) return output;
        output.push({
            field: block.name,
            values: block.values,
        });
        return output;
    }, []);

type MissingValue = ValuesByFieldT & {
    filterKey: DashboardFilterT['key'];
};

export type MissingValueByScopeAndField = Record<
    ScopeT['name'],
    MissingValue[] | undefined
>;
/*
    For each scope, if a field is not covered by the filters,
    it returns the values that should be present in the filters
*/
export const getMissingValuesByScopeAndField = (
    userScopes: UserScopeT[],
    filters: DashboardFilterT[],
): MissingValueByScopeAndField =>
    userScopes.reduce((output: MissingValueByScopeAndField, scope) => {
        const fields: MissingValue[] = [];

        if (scope.blocks) {
            const items = getScopeValuesByField(scope);

            items.forEach(({ field, values }) => {
                const filter = filters.find(
                    (item) => getTableName(item.field) === field,
                );
                if (filter && filter.value !== `scope:${scope.name}`) {
                    // We're asking the user to update the filter values if it's different from the scope
                    // We could be more precise and check if the values in the scope are selected,
                    // and, if there are more values that are selected, check they are authorized
                    fields.push({
                        field: filter.field,
                        values,
                        filterKey: filter.key,
                    });
                }
            });
        }

        return {
            ...output,
            [scope.name]: fields,
        };
    }, {});

// Some scopes have "Périmètre" in their names (ex: "Périmètre de la population"),
// so we remove it to prevent word duplication
export const removePerimeterFromScope = (
    scope: string,
    t: (arg1: string) => string,
): string => {
    const perimeter = new RegExp(t('dashboardScopesAlerts.perimeter'), 'i');

    return scope.replace(perimeter, '');
};

/**
 * This function aims to format the missing filters or values into a readable string for users in the scopes alerts.
   It takes into account that it is sometimes needed to add every filter / value.
 * @param blocks Blocks to format
 * @param junction And / or junction for lists with at least 2 items
 * @param t translate
 * @param pluralJunction if true, add "the" before the blocks and after the junction
 * @returns A formatted string
 */
export const formatMissingFiltersOrValues = (
    blocks: string[],
    junction: 'and' | 'or',
    t: TFunction<'translation', undefined>,
    pluralJunction: boolean = false,
) => {
    if (blocks.length < 2) return blocks[0] ?? '';

    const junctionKey = `sentence:${junction}${pluralJunction ? 'The' : ''}`;
    const junctionTranslation = t(junctionKey);
    const commaTranslation = pluralJunction ? `, ${t('sentence:the')} ` : ', ';

    return blocks.length === 2
        ? blocks.join(` ${junctionTranslation} `)
        : `${blocks.slice(0, -1).join(commaTranslation)} ${junctionTranslation} ${blocks.at(-1)}`;
};

type ScopeExpressionType = 'any' | 'all' | 'none';

/**
 * Determines if the scope expression is 'any', 'all' or 'none'.
 * @param {ScopeT} scope - The scope configuration
 * @returns {ScopeExpressionType | null } - The scope expression key
 */
export const getScopeExpressionKey = (
    scope: ScopeT,
): ScopeExpressionType | null => {
    const { expression } = scope;
    if (!expression) return null;
    if (isAnyExpression(expression)) {
        return 'any';
    }
    if (isAllExpression(expression)) {
        return 'all';
    }
    return 'none';
};
